import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { addDays, format, parseISO, isValid } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Leaf, Calendar, ArrowLeft, GripVertical, X, Check, Calculator, PenTool, Search, Plus, BookOpen, ExternalLink, Trophy, Clock, Target, StickyNote, LayoutGrid, CalendarDays, Printer, ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import ChildSelector from '@/components/ChildSelector';
import FamilyLink from '@/components/FamilyLink';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

export default function PlannerPage() {
  const { user, authHeaders, childParam, activeChildName, progressVersion, bumpProgress } = useAuth();
  const navigate = useNavigate();
  const [planner, setPlanner] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState([]);
  const [dayNotes, setDayNotes] = useState({});  // key: `w${week}d${day}` → string
  const [editingNote, setEditingNote] = useState(null); // `w${week}d${day}` of cell being edited
  const [noteDraft, setNoteDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'month'
  const [searchTerm, setSearchTerm] = useState('');
  const [termFilter, setTermFilter] = useState('all'); // 'all' | 1 | 2
  const [subjectFilter, setSubjectFilter] = useState('all'); // 'all' | 'maths' | 'english'
  const [dragging, setDragging] = useState(null);

  // Setup form
  const [setupForm, setSetupForm] = useState({
    title: 'My Term Planner',
    term_number: 2,
    term_start: '2026-04-13',
    weeks: 10,
    children: user?.children || [],
    goals: ['']
  });

  // Edit-planner dialog state
  const [editingPlanner, setEditingPlanner] = useState(false);
  const [editForm, setEditForm] = useState({ term_number: 2, term_start: '' });

  const loadData = useCallback(async () => {
    try {
      const [plannerRes, lessonsRes, progressRes] = await Promise.all([
        axios.get(`${API}/planner${childParam}`, { headers: authHeaders }),
        axios.get(`${API}/lessons`),
        axios.get(`${API}/progress${childParam}`, { headers: authHeaders }),
      ]);
      if (plannerRes.data) {
        setPlanner(plannerRes.data);
        setSetupMode(false);
        // Default the Available-Lessons term filter to the planner's term so
        // users see the right lessons first without needing to click.
        const plannerTerm = plannerRes.data.term_number;
        if (plannerTerm) setTermFilter(plannerTerm);
        // Load day notes (only meaningful when planner exists)
        try {
          const notesRes = await axios.get(`${API}/planner/day-notes${childParam}`, { headers: authHeaders });
          const map = {};
          (notesRes.data || []).forEach(n => { map[`w${n.week}d${n.day}`] = n.note; });
          setDayNotes(map);
        } catch { /* non-fatal */ }
      } else {
        setSetupMode(true);
      }
      setLessons(lessonsRes.data);
      setProgress(progressRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [authHeaders, childParam]);

  const saveDayNote = async (week, day) => {
    const note = noteDraft.trim();
    try {
      await axios.put(`${API}/planner/day-notes${childParam}`, { week, day, note }, { headers: authHeaders });
      const key = `w${week}d${day}`;
      setDayNotes(prev => {
        const next = { ...prev };
        if (note) next[key] = note; else delete next[key];
        return next;
      });
      setEditingNote(null);
      setNoteDraft('');
    } catch { toast.error('Failed to save note'); }
  };

  const beginEditNote = (week, day) => {
    const key = `w${week}d${day}`;
    setEditingNote(key);
    setNoteDraft(dayNotes[key] || '');
  };

  useEffect(() => { loadData(); }, [loadData]);

  const handleSetup = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/planner`, {
        ...setupForm,
        goals: setupForm.goals.filter(g => g.trim()),
        child: activeChildName || undefined,
      }, { headers: authHeaders });
      setPlanner({ ...res.data, slots: [] });
      setSetupMode(false);
      const plannerTerm = res.data?.term_number;
      if (plannerTerm) setTermFilter(plannerTerm);
      toast.success('Planner created! Start scheduling your lessons.');
    } catch (err) {
      toast.error('Failed to create planner');
    }
  };

  const handleUpdatePlanner = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        term_number: parseInt(editForm.term_number),
        term_start: editForm.term_start,
      };
      const res = await axios.put(`${API}/planner${childParam}`, payload, { headers: authHeaders });
      setPlanner(res.data);
      const plannerTerm = res.data?.term_number;
      if (plannerTerm) setTermFilter(plannerTerm);
      setEditingPlanner(false);
      toast.success(`Planner updated — now showing Term ${payload.term_number}`);
    } catch (err) {
      toast.error('Failed to update planner');
    }
  };

  const scheduledRefs = new Set((planner?.slots || []).map(s => s.lesson_reference));

  const availableLessons = lessons.filter(l => {
    if (scheduledRefs.has(l.reference)) return false;
    if (searchTerm && !l.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (termFilter !== 'all' && (l.term || 2) !== termFilter) return false;
    if (subjectFilter !== 'all' && l.subject !== subjectFilter) return false;
    return true;
  });

  const completedRefs = new Set(progress.filter(p => p.completed).map(p => p.lesson_reference));
  const isSlotCompleted = (slot) => completedRefs.has(slot.lesson_reference);
  const getSlotsForCell = (week, day) => (planner?.slots || []).filter(s => s.week === week && s.day === day && !isSlotCompleted(s));
  const getCompletedSlotsForWeek = (week) => (planner?.slots || []).filter(s => s.week === week && isSlotCompleted(s));
  const lessonByRef = (ref) => lessons.find(l => l.reference === ref);

  // ---- Date helpers ----
  // Term start is stored as YYYY-MM-DD; treat it as the Monday of Week 1.
  const termStart = planner?.term_start ? parseISO(planner.term_start) : null;
  const dateForCell = (week, day) => {
    if (!termStart || !isValid(termStart)) return null;
    return addDays(termStart, (week - 1) * 7 + (day - 1));
  };
  const fmtDayPill = (d) => (d ? format(d, 'd MMM') : '');
  const fmtDayLong = (d) => (d ? format(d, 'EEE d MMM') : '');

  // ---- Month view derivation ----
  // Group every (week, day) cell by its calendar month so the user can browse
  // a familiar month-at-a-time layout while still respecting the term's
  // Mon–Fri school structure.
  const allCells = (() => {
    if (!planner || !termStart) return [];
    const cells = [];
    for (let w = 1; w <= planner.weeks; w++) {
      for (let d = 1; d <= 5; d++) {
        const date = dateForCell(w, d);
        if (date) cells.push({ week: w, day: d, date });
      }
    }
    return cells;
  })();
  const monthsInTerm = (() => {
    const seen = new Map();
    for (const c of allCells) {
      const key = format(c.date, 'yyyy-MM');
      if (!seen.has(key)) seen.set(key, c.date);
    }
    return Array.from(seen.entries()).map(([key, date]) => ({ key, date }));
  })();
  const [activeMonthKey, setActiveMonthKey] = useState(null);
  // Default the month picker to the month containing the currently-selected week
  useEffect(() => {
    if (!termStart || activeMonthKey) return;
    const d = dateForCell(currentWeek, 1);
    if (d) setActiveMonthKey(format(d, 'yyyy-MM'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planner]);
  const cellsForActiveMonth = activeMonthKey
    ? allCells.filter(c => format(c.date, 'yyyy-MM') === activeMonthKey)
    : [];
  const weeksInActiveMonth = Array.from(new Set(cellsForActiveMonth.map(c => c.week))).sort((a, b) => a - b);
  const activeMonthLabel = activeMonthKey
    ? format(monthsInTerm.find(m => m.key === activeMonthKey)?.date || new Date(), 'MMMM yyyy')
    : '';
  const activeMonthIdx = monthsInTerm.findIndex(m => m.key === activeMonthKey);
  const goPrevMonth = () => {
    if (activeMonthIdx > 0) setActiveMonthKey(monthsInTerm[activeMonthIdx - 1].key);
  };
  const goNextMonth = () => {
    if (activeMonthIdx >= 0 && activeMonthIdx < monthsInTerm.length - 1) {
      setActiveMonthKey(monthsInTerm[activeMonthIdx + 1].key);
    }
  };

  const handlePrint = () => window.print();

  const handleDragStart = (lesson) => setDragging(lesson);

  const handleDrop = async (week, day) => {
    if (!dragging) return;
    try {
      const res = await axios.post(`${API}/planner/slots${childParam}`, {
        week, day,
        subject: dragging.subject,
        lesson_title: dragging.title,
        lesson_reference: dragging.reference,
        duration_minutes: dragging.duration,
        needs: (dragging.materials || []).join(', ')
      }, { headers: authHeaders });
      setPlanner(p => ({ ...p, slots: [...(p.slots || []), res.data] }));
      toast.success(`${dragging.title} scheduled for Week ${week}, ${DAYS[day - 1]}`);
    } catch { toast.error('Failed to schedule lesson'); }
    setDragging(null);
  };

  const handleRemoveSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/planner/slots/${slotId}`, { headers: authHeaders });
      setPlanner(p => ({ ...p, slots: (p.slots || []).filter(s => s.id !== slotId) }));
      toast.success('Lesson removed from schedule');
    } catch { toast.error('Failed to remove'); }
  };

  const handleToggleComplete = async (slot) => {
    const currentlyDone = completedRefs.has(slot.lesson_reference);
    const nowDone = !currentlyDone;
    try {
      await axios.post(`${API}/progress`, {
        lesson_reference: slot.lesson_reference,
        phase: 'plenary',
        completed: nowDone,
        child: activeChildName || undefined,
      }, { headers: authHeaders });
      // Update local progress state
      setProgress(prev => {
        const existing = prev.find(p => p.lesson_reference === slot.lesson_reference);
        if (existing) {
          return prev.map(p => p.lesson_reference === slot.lesson_reference ? { ...p, completed: nowDone } : p);
        }
        return [...prev, { lesson_reference: slot.lesson_reference, completed: nowDone }];
      });
      bumpProgress();
      toast.success(nowDone ? 'Marked complete' : 'Marked incomplete');
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><div className="animate-pulse text-fern font-semibold">Loading planner...</div></div>;

  // Setup wizard
  if (setupMode) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40">
          <div className="max-w-3xl mx-auto px-4 flex items-center h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <Leaf className="w-7 h-7 text-fern" strokeWidth={2.5} />
              <span className="font-heading font-bold text-lg text-fern">NestWise</span>
            </Link>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8 animate-fadeInUp">
            <Calendar className="w-12 h-12 text-fern mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-bold text-fern-dark">Set Up Your Term Planner</h1>
            <p className="text-slate-600 mt-2">Let us organise your homeschool term. This takes about 2 minutes.</p>
          </div>
          <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl animate-fadeInUp stagger-2">
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSetup} className="space-y-6">
                <div>
                  <Label htmlFor="planner-title">Planner Title</Label>
                  <Input data-testid="setup-title" id="planner-title" value={setupForm.title} onChange={e => setSetupForm(p => ({ ...p, title: e.target.value }))}
                    className="rounded-xl mt-1 bg-slate-50" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="term-number">Term Number</Label>
                    <select data-testid="setup-term-number" id="term-number" value={setupForm.term_number}
                      onChange={e => setSetupForm(p => ({ ...p, term_number: parseInt(e.target.value) }))}
                      className="w-full rounded-xl mt-1 bg-slate-50 border border-input px-3 py-2 text-sm">
                      <option value={1}>Term 1</option>
                      <option value={2}>Term 2</option>
                      <option value={3}>Term 3</option>
                      <option value={4}>Term 4</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="term-start">Term Start Date</Label>
                    <Input data-testid="setup-start" id="term-start" type="date" value={setupForm.term_start}
                      onChange={e => setSetupForm(p => ({ ...p, term_start: e.target.value }))} className="rounded-xl mt-1 bg-slate-50" />
                  </div>
                  <div>
                    <Label htmlFor="weeks">Number of Weeks</Label>
                    <Input data-testid="setup-weeks" id="weeks" type="number" min={1} max={20} value={setupForm.weeks}
                      onChange={e => setSetupForm(p => ({ ...p, weeks: parseInt(e.target.value) || 10 }))} className="rounded-xl mt-1 bg-slate-50" />
                  </div>
                </div>
                <div>
                  <Label>Term Goals (optional)</Label>
                  {setupForm.goals.map((g, i) => (
                    <div key={i} className="flex gap-2 mt-2">
                      <Input data-testid={`setup-goal-${i}`} placeholder={`Goal ${i + 1}`} value={g}
                        onChange={e => { const goals = [...setupForm.goals]; goals[i] = e.target.value; setSetupForm(p => ({ ...p, goals })); }}
                        className="rounded-xl bg-slate-50" />
                      {i === setupForm.goals.length - 1 && (
                        <Button type="button" variant="outline" size="sm" onClick={() => setSetupForm(p => ({ ...p, goals: [...p.goals, ''] }))}
                          className="rounded-xl border-slate-200"><Plus className="w-4 h-4" /></Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button data-testid="setup-submit" type="submit" className="w-full bg-fern hover:bg-fern-light text-white rounded-full py-3 font-medium transition-all hover:scale-[1.02]">
                  Create My Planner
                </Button>
              </form>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const plannerTitle = `My Term ${planner.term_number || 2} Planner`;

  const totalSlots = (planner?.slots || []).length;
  const completedSlots = (planner?.slots || []).filter(isSlotCompleted).length;
  const completionPct = totalSlots > 0 ? Math.round((completedSlots / totalSlots) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40 no-print">
        <div className="max-w-[1400px] mx-auto px-4 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center">
              <BrandMark size={36} />
            </Link>
          </div>
          <nav className="flex items-center gap-2">
            <Link to="/dashboard"><Button variant="ghost" className="text-slate-600 hover:text-fern rounded-lg gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Button></Link>
            <Link to="/lessons"><Button variant="ghost" className="text-slate-600 hover:text-fern rounded-lg gap-1"><BookOpen className="w-4 h-4" /> <span className="hidden sm:inline">Lessons</span></Button></Link>
            <FamilyLink />
            <ChildSelector />
          </nav>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 print-only-show">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-fern-dark">{plannerTitle}</h1>
              <Button
                data-testid="planner-edit-btn"
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditForm({
                    term_number: planner.term_number || 2,
                    term_start: planner.term_start || '',
                  });
                  setEditingPlanner(true);
                }}
                className="no-print rounded-full border-fern/30 text-fern hover:bg-fern/5 gap-1.5"
              >
                <Settings className="w-3.5 h-3.5" /> Term settings
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="font-semibold text-fern-dark text-sm" data-testid="planner-term-badge">Term {planner.term_number || 2}</span>
              <div className="h-1.5 w-32 rounded-full bg-fern-100 overflow-hidden" data-testid="planner-progress-bar">
                <div className="h-full bg-fern rounded-full transition-all" style={{ width: `${completionPct}%` }} />
              </div>
              <span className="text-sm text-slate-500">
                {completedSlots} of {totalSlots} lessons{totalSlots > 0 && ` · ${completionPct}%`}
              </span>
              {termStart && (
                <Badge className="hidden sm:inline-flex bg-slate-50 text-slate-500 border-0 rounded-full text-xs font-normal">
                  Term begins {format(termStart, 'EEE d MMM yyyy')}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 no-print">
            {/* View toggle */}
            <div data-testid="view-toggle" className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-sm">
              <button
                data-testid="view-week"
                onClick={() => setViewMode('week')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${viewMode === 'week' ? 'bg-fern text-white shadow' : 'text-slate-500 hover:text-fern'}`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Week
              </button>
              <button
                data-testid="view-month"
                onClick={() => setViewMode('month')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${viewMode === 'month' ? 'bg-fern text-white shadow' : 'text-slate-500 hover:text-fern'}`}
              >
                <CalendarDays className="w-3.5 h-3.5" /> Month
              </button>
            </div>

            {/* Week selector — only in week view */}
            {viewMode === 'week' && (
              <div className="flex flex-wrap gap-1.5">
                {[1, 2, 3, 4, 5].map(w => (
                  <Button key={w} data-testid={`week-${w}`} variant={currentWeek === w ? 'default' : 'outline'} size="sm"
                    onClick={() => setCurrentWeek(w)}
                    className={`rounded-full ${currentWeek === w ? 'bg-fern text-white' : 'border-slate-200 text-slate-600'}`}>
                    W{w}
                  </Button>
                ))}
                {[6, 7, 8, 9, 10].map(w => (
                  <Button key={w} data-testid={`week-${w}`} variant={currentWeek === w ? 'default' : 'outline'} size="sm"
                    onClick={() => setCurrentWeek(w)}
                    className={`rounded-full hidden sm:inline-flex ${currentWeek === w ? 'bg-fern text-white' : 'border-slate-200 text-slate-600'}`}>
                    W{w}
                  </Button>
                ))}
              </div>
            )}

            {/* Month nav — only in month view */}
            {viewMode === 'month' && (
              <div className="flex items-center gap-1">
                <Button data-testid="month-prev" variant="outline" size="icon"
                  onClick={goPrevMonth} disabled={activeMonthIdx <= 0}
                  className="rounded-full h-8 w-8 border-slate-200 text-slate-600">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span data-testid="month-label" className="px-3 text-sm font-semibold text-fern-dark min-w-[140px] text-center">
                  {activeMonthLabel}
                </span>
                <Button data-testid="month-next" variant="outline" size="icon"
                  onClick={goNextMonth} disabled={activeMonthIdx < 0 || activeMonthIdx >= monthsInTerm.length - 1}
                  className="rounded-full h-8 w-8 border-slate-200 text-slate-600">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Print */}
            <Button data-testid="planner-print" variant="outline" size="sm" onClick={handlePrint}
              className="rounded-full border-fern/30 text-fern hover:bg-fern/5 gap-1.5">
              <Printer className="w-3.5 h-3.5" /> Print
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left panel - available lessons (hidden in print + month view) */}
          <div className={`lg:col-span-1 no-print ${viewMode === 'month' ? 'hidden lg:hidden' : ''}`}>
            <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl sticky top-20">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base text-fern-dark">Available Lessons</CardTitle>
                <CardDescription className="text-xs">Drag lessons to the calendar</CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="relative mb-3">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input data-testid="planner-search" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    className="pl-8 rounded-lg bg-slate-50 text-sm h-8" />
                </div>
                <div className="flex items-center gap-1 mb-2" data-testid="planner-term-filter">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 1, label: 'Term 1' },
                    { key: 2, label: 'Term 2' },
                    { key: 3, label: 'Term 3' },
                    { key: 4, label: 'Term 4' },
                  ].map(opt => (
                    <button
                      key={String(opt.key)}
                      type="button"
                      onClick={() => setTermFilter(opt.key)}
                      data-testid={`term-filter-${opt.key}`}
                      className={`flex-1 text-[10px] font-semibold rounded-full px-2 py-1 transition-colors ${
                        termFilter === opt.key
                          ? 'bg-fern text-white shadow-sm'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1 mb-3" data-testid="planner-subject-filter">
                  {[
                    { key: 'all', label: 'All', activeClass: 'bg-fern text-white' },
                    { key: 'maths', label: 'Maths', activeClass: 'bg-moana text-white' },
                    { key: 'english', label: 'English', activeClass: 'bg-fern text-white' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSubjectFilter(opt.key)}
                      data-testid={`subject-filter-${opt.key}`}
                      className={`flex-1 text-[10px] font-semibold rounded-full px-2 py-1 transition-colors ${
                        subjectFilter === opt.key
                          ? `${opt.activeClass} shadow-sm`
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {availableLessons.map(l => (
                    <div key={l.reference} data-testid={`draggable-${l.reference}`}
                      draggable
                      onDragStart={() => handleDragStart(l)}
                      onDragEnd={() => setDragging(null)}
                      className="bg-white border border-slate-100 rounded-xl p-3 cursor-grab active:cursor-grabbing hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Badge className={`${l.subject === 'maths' ? 'bg-moana-50 text-moana' : 'bg-fern-50 text-fern'} border-0 rounded-full text-[10px] px-1.5 py-0`}>
                              {l.subject === 'maths' ? <Calculator className="w-3 h-3" /> : <PenTool className="w-3 h-3" />}
                            </Badge>
                            <span className="text-[10px] text-slate-400">T{l.term || 2} · W{l.week}D{l.day}</span>
                          </div>
                          <p className="text-xs font-medium text-fern-dark truncate">{l.title}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">{l.duration}min</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {availableLessons.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-4">All lessons scheduled!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Calendar grid */}
          <div className={viewMode === 'month' ? 'lg:col-span-4' : 'lg:col-span-3'}>
            {viewMode === 'week' && (
            <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl print-shadow-none">
              <CardContent className="p-4 sm:p-6">
                <h2 className="font-heading text-lg font-semibold text-fern-dark mb-4">
                  Week {currentWeek}
                  {termStart && (
                    <span className="ml-3 text-sm font-normal text-slate-500">
                      {format(dateForCell(currentWeek, 1), 'd MMM')} – {format(dateForCell(currentWeek, 5), 'd MMM yyyy')}
                    </span>
                  )}
                </h2>
                <div className="grid grid-cols-5 gap-3">
                  {DAYS.map((day, di) => {
                    const dayIdx = di + 1;
                    const noteKey = `w${currentWeek}d${dayIdx}`;
                    const noteText = dayNotes[noteKey];
                    const isEditing = editingNote === noteKey;
                    const cellDate = dateForCell(currentWeek, dayIdx);
                    return (
                    <div key={di} className="min-h-[180px]">
                      <div className="text-center mb-2">
                        <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{DAY_SHORT[di]}</div>
                        {cellDate && (
                          <div data-testid={`day-date-w${currentWeek}-d${dayIdx}`} className="text-[11px] text-slate-400 font-medium mt-0.5">
                            {fmtDayPill(cellDate)}
                          </div>
                        )}
                      </div>
                      {/* Day note */}
                      {isEditing ? (
                        <div className="mb-2">
                          <Input
                            data-testid={`day-note-input-w${currentWeek}-d${dayIdx}`}
                            autoFocus
                            value={noteDraft}
                            onChange={e => setNoteDraft(e.target.value)}
                            onBlur={() => saveDayNote(currentWeek, dayIdx)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveDayNote(currentWeek, dayIdx);
                              if (e.key === 'Escape') { setEditingNote(null); setNoteDraft(''); }
                            }}
                            placeholder="e.g. Field trip"
                            className="text-xs rounded-lg h-7 px-2"
                          />
                        </div>
                      ) : noteText ? (
                        <button
                          data-testid={`day-note-w${currentWeek}-d${dayIdx}`}
                          onClick={() => beginEditNote(currentWeek, dayIdx)}
                          className="w-full mb-2 px-2 py-1 rounded-lg bg-kowhai-50 border border-kowhai/30 text-[10px] text-kowhai-dark text-left flex items-center gap-1 hover:bg-kowhai-100 transition-colors group"
                        >
                          <StickyNote className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate flex-1">{noteText}</span>
                        </button>
                      ) : (
                        <button
                          data-testid={`add-day-note-w${currentWeek}-d${dayIdx}`}
                          onClick={() => beginEditNote(currentWeek, dayIdx)}
                          className="w-full mb-2 px-2 py-1 rounded-lg border border-dashed border-slate-200 text-[10px] text-slate-400 hover:border-kowhai/40 hover:text-kowhai-dark transition-colors flex items-center justify-center gap-1 opacity-0 hover:opacity-100 focus:opacity-100"
                          style={{ opacity: 0 }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '0'}
                        >
                          <Plus className="w-3 h-3" /> Add note
                        </button>
                      )}
                      <div
                        data-testid={`drop-w${currentWeek}-d${dayIdx}`}
                        onDragOver={e => e.preventDefault()}
                        onDrop={() => handleDrop(currentWeek, dayIdx)}
                        className={`min-h-[150px] rounded-2xl border-2 border-dashed p-2 space-y-2 transition-colors duration-200 ${dragging ? 'border-fern/40 bg-fern/5' : 'border-slate-200 bg-slate-50/50'}`}
                      >
                        {getSlotsForCell(currentWeek, dayIdx).map(slot => {
                          const fullLesson = lessonByRef(slot.lesson_reference);
                          return (
                          <HoverCard key={slot.id} openDelay={250} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <div data-testid={`slot-${slot.id}`}
                                className={`rounded-xl p-2.5 text-xs relative group transition-all cursor-pointer hover:shadow-md hover:-translate-y-0.5 ${slot.subject === 'maths' ? 'bg-moana-50 border border-moana/20' : 'bg-fern-50 border border-fern/20'}`}
                                onClick={() => navigate(`/lessons/${slot.lesson_reference}`)}>
                                <div className="flex items-center justify-between mb-1">
                                  <Badge className={`${slot.subject === 'maths' ? 'bg-moana/10 text-moana' : 'bg-fern/10 text-fern'} border-0 rounded-full text-[9px] px-1.5 py-0`}>
                                    {slot.subject === 'maths' ? 'M' : 'E'}
                                  </Badge>
                                  <div className="flex items-center gap-1">
                                    <button data-testid={`toggle-${slot.id}`} onClick={(e) => { e.stopPropagation(); handleToggleComplete(slot); }}
                                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSlotCompleted(slot) ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300 hover:border-fern'}`}>
                                      {isSlotCompleted(slot) && <Check className="w-3 h-3" />}
                                    </button>
                                    <button data-testid={`remove-slot-${slot.id}`}
                                      onClick={(e) => { e.stopPropagation(); handleRemoveSlot(slot.id); }}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                                <p className="font-medium text-fern-dark leading-tight mb-1.5 line-clamp-2">{slot.lesson_title}</p>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-400">{slot.duration_minutes}m</span>
                                  <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-moana transition-colors" />
                                </div>
                              </div>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-80 rounded-2xl shadow-xl border-0" side="right" align="start">
                              <div className="space-y-2">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <p className={`text-[10px] uppercase tracking-wider font-semibold ${slot.subject === 'maths' ? 'text-moana' : 'text-fern'}`}>
                                      {slot.subject === 'maths' ? 'Pāngarau · Maths' : 'Reo Pākehā · English'} · Term {fullLesson?.term || 2} · W{fullLesson?.week || slot.week} D{fullLesson?.day || slot.day}
                                    </p>
                                    <h4 className="font-heading font-semibold text-fern-dark text-sm leading-tight mt-0.5">{slot.lesson_title}</h4>
                                    {fullLesson?.teReoTitle && <p className="text-xs text-slate-500 italic">{fullLesson.teReoTitle}</p>}
                                  </div>
                                </div>
                                {fullLesson?.learningObjective && (
                                  <div className="flex items-start gap-2 pt-2 border-t border-slate-100">
                                    <Target className="w-3.5 h-3.5 text-fern flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-slate-700 leading-relaxed">{fullLesson.learningObjective}</p>
                                  </div>
                                )}
                                <div className="flex items-center gap-3 text-xs text-slate-500 pt-1">
                                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {slot.duration_minutes} min</span>
                                  {isSlotCompleted(slot) ? (
                                    <span className="inline-flex items-center gap-1 text-green-600"><Check className="w-3 h-3" /> Completed</span>
                                  ) : (
                                    <span className="text-slate-400">Click to open</span>
                                  )}
                                </div>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Month view */}
            {viewMode === 'month' && (
              <Card data-testid="month-view" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl print-shadow-none">
                <CardContent className="p-4 sm:p-6">
                  <h2 className="font-heading text-lg font-semibold text-fern-dark mb-4">{activeMonthLabel}</h2>
                  {weeksInActiveMonth.length === 0 ? (
                    <p className="text-sm text-slate-400 italic">No school days in this month yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <div className="min-w-[720px]">
                        {/* Day-of-week header */}
                        <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: '60px repeat(5, 1fr)' }}>
                          <div />
                          {DAY_SHORT.map(d => (
                            <div key={d} className="text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{d}</div>
                          ))}
                        </div>
                        {/* Each week row */}
                        {weeksInActiveMonth.map(w => (
                          <div key={w} data-testid={`month-row-w${w}`}
                            className="grid gap-2 mb-2 items-stretch"
                            style={{ gridTemplateColumns: '60px repeat(5, 1fr)' }}>
                            <button
                              onClick={() => { setViewMode('week'); setCurrentWeek(w); }}
                              className="rounded-xl bg-fern/5 hover:bg-fern/10 text-fern text-xs font-bold flex items-center justify-center transition-colors"
                              title={`Open Week ${w}`}>
                              W{w}
                            </button>
                            {[1, 2, 3, 4, 5].map(di => {
                              const cell = cellsForActiveMonth.find(c => c.week === w && c.day === di);
                              if (!cell) {
                                return <div key={di} className="rounded-xl bg-slate-50/40 min-h-[80px]" />;
                              }
                              const slotsHere = (planner?.slots || []).filter(s => s.week === w && s.day === di);
                              const noteKey = `w${w}d${di}`;
                              const noteText = dayNotes[noteKey];
                              return (
                                <div
                                  key={di}
                                  data-testid={`month-cell-w${w}-d${di}`}
                                  onDragOver={e => e.preventDefault()}
                                  onDrop={() => handleDrop(w, di)}
                                  className={`rounded-xl border ${dragging ? 'border-fern/40 bg-fern/5' : 'border-slate-200 bg-white'} p-2 min-h-[80px] transition-colors`}
                                >
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[11px] font-bold text-slate-700">{format(cell.date, 'd MMM')}</span>
                                    {noteText && (
                                      <span className="inline-flex items-center gap-0.5 text-[9px] text-kowhai-dark bg-kowhai-50 rounded-full px-1.5 py-0.5 max-w-[80px] truncate" title={noteText}>
                                        <StickyNote className="w-2.5 h-2.5" />{noteText}
                                      </span>
                                    )}
                                  </div>
                                  <div className="space-y-1">
                                    {slotsHere.map(slot => {
                                      const done = isSlotCompleted(slot);
                                      return (
                                        <button
                                          key={slot.id}
                                          data-testid={`month-slot-${slot.id}`}
                                          onClick={() => navigate(`/lessons/${slot.lesson_reference}`)}
                                          className={`w-full text-left rounded-md px-1.5 py-1 text-[10px] leading-tight transition-all ${done ? 'bg-green-50 border border-green-200/60 text-slate-500 line-through' : (slot.subject === 'maths' ? 'bg-moana-50 border border-moana/20 text-moana-dark' : 'bg-fern-50 border border-fern/20 text-fern-dark')}`}
                                        >
                                          <div className="flex items-center gap-1">
                                            {done && <Check className="w-2.5 h-2.5 text-green-600 flex-shrink-0" />}
                                            <span className="font-semibold uppercase text-[8px] tracking-wider opacity-70">
                                              {slot.subject === 'maths' ? 'M' : 'E'}
                                            </span>
                                            <span className="truncate flex-1">{slot.lesson_title}</span>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Completed lessons area */}
            {viewMode === 'week' && getCompletedSlotsForWeek(currentWeek).length > 0 && (
              <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl mt-6">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-5 h-5 text-kowhai" />
                    <h2 className="font-heading text-lg font-semibold text-fern-dark">Completed This Week</h2>
                    <Badge className="bg-green-100 text-green-700 border-0 rounded-full text-xs ml-auto">
                      {getCompletedSlotsForWeek(currentWeek).length} done
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {getCompletedSlotsForWeek(currentWeek).map(slot => (
                      <div key={slot.id} data-testid={`completed-slot-${slot.id}`}
                        className="rounded-2xl p-4 bg-green-50/60 border border-green-200/60 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
                        onClick={() => navigate(`/lessons/${slot.lesson_reference}`)}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center">
                              <Check className="w-3.5 h-3.5" />
                            </div>
                            <Badge className={`${slot.subject === 'maths' ? 'bg-moana/10 text-moana' : 'bg-fern/10 text-fern'} border-0 rounded-full text-[10px]`}>
                              {slot.subject === 'maths' ? 'Maths' : 'English'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-slate-400">{DAYS[slot.day - 1]}</span>
                            <button data-testid={`uncomplete-${slot.id}`}
                              onClick={(e) => { e.stopPropagation(); handleToggleComplete(slot); }}
                              className="opacity-0 group-hover:opacity-100 text-[10px] text-slate-400 hover:text-orange-500 transition-all ml-1 underline">
                              undo
                            </button>
                          </div>
                        </div>
                        <p className="text-sm font-medium text-fern-dark mb-1">{slot.lesson_title}</p>
                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <span>{slot.duration_minutes} min</span>
                          <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-moana transition-colors" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Edit-planner modal */}
      {editingPlanner && (
        <div
          data-testid="planner-edit-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm no-print"
          onClick={() => setEditingPlanner(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 w-[90%] max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-heading text-xl font-bold text-fern-dark mb-1">Term settings</h3>
            <p className="text-xs text-slate-500 mb-4">Update which term you're planning for and when it starts.</p>
            <form onSubmit={handleUpdatePlanner} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit-term-number">Term</Label>
                  <select data-testid="edit-term-number" id="edit-term-number" value={editForm.term_number}
                    onChange={e => setEditForm(p => ({ ...p, term_number: parseInt(e.target.value) }))}
                    className="w-full rounded-xl mt-1 bg-slate-50 border border-input px-3 py-2 text-sm">
                    <option value={1}>Term 1</option>
                    <option value={2}>Term 2</option>
                    <option value={3}>Term 3</option>
                    <option value={4}>Term 4</option>
                  </select>
                  <p className="text-[11px] text-slate-400 mt-1">Sets which term's lessons you're planning from.</p>
                </div>
                <div>
                  <Label htmlFor="edit-start">Term Start</Label>
                  <Input data-testid="edit-start" id="edit-start" type="date" value={editForm.term_start}
                    onChange={e => setEditForm(p => ({ ...p, term_start: e.target.value }))}
                    className="rounded-xl mt-1 bg-slate-50" />
                  <p className="text-[11px] text-slate-400 mt-1">Used to lay out weeks and dates on the calendar.</p>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditingPlanner(false)}
                  className="flex-1 rounded-full border-slate-200">
                  Cancel
                </Button>
                <Button data-testid="edit-submit" type="submit" className="flex-1 rounded-full bg-fern hover:bg-fern-light text-white">
                  Save changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
